import { useState } from 'react';
import { c, card, tbl } from '../../theme';
import { fmtCount } from './format';

// Where the calls came from: volume per city, the yard that serves it, and
// the share of the window.
//
// The old Board draws this beside a map. The table is the part that carries
// the numbers, and it is here without one: a map needs tile images from a
// third-party host, and this portal's Content-Security-Policy is pinned with
// no CDNs at all (see CLAUDE.md). Widening img-src is a decision about a live
// portal's security policy, not a layout choice, so it is Athens's to make.
//
// The yard is a DERIVED attribution, not an Athens statement of fact: the old
// app worked it out from one month's volume, and a city split between yards
// is named under whichever served most of it. Where that share is under 80%
// the row says so, because "Pomona · SALT LAKE" reads as certain and, at 60%,
// is not.
const WEAK_SHARE = 80;

export default function CityVolume({ cities, rangeLabel }) {
  const [all, setAll] = useState(false);
  const list = cities || [];
  const shown = all ? list : list.slice(0, 12);
  return (
    <section style={s.card} aria-label="Volume by city">
      <h2 style={s.h2}>Volume by city <span style={s.sub}>({rangeLabel})</span></h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>City</th><th style={s.th}>Yard</th>
              <th style={s.thNum}>Volume</th><th style={s.thNum}>% of total</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.cityKey}>
                <td style={{ ...s.td, fontWeight: 550 }}>{r.city}</td>
                <td style={s.td}>
                  {r.yard
                    ? <>{r.yard}{r.yardSharePct != null && r.yardSharePct < WEAK_SHARE && (
                        <span style={s.weak} title={`This city's calls split between yards; ${r.yard} served ${r.yardSharePct}% of them when the mapping was derived`}>
                          {' '}~{Math.round(r.yardSharePct)}%
                        </span>)}</>
                    : <span style={{ color: c.textFaint }}>—</span>}
                </td>
                <td style={s.num}>{fmtCount(r.volume)}</td>
                <td style={s.num}>{r.sharePct == null ? '—' : `${r.sharePct}%`}</td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={4} style={s.empty}>No calls with a city in this window.</td></tr>}
          </tbody>
        </table>
      </div>
      {list.length > 12 && (
        <button type="button" onClick={() => setAll((v) => !v)} style={s.more}>
          {all ? 'Show the top 12' : `Show all ${list.length} cities`}
        </button>
      )}
      <p style={s.foot}>
        The top ten cities among Voice, VIP and Callback calls an agent handled, by the city resolved for
        the caller; % of total is of the window’s Handled. The yard is derived from one month of service
        volume, not stated by Athens; a share is shown where the city splits between yards.
      </p>
    </section>
  );
}

const s = {
  card:  { ...card.base, padding: '14px 18px 12px' },
  h2:    { fontSize: '13px', fontWeight: 650, color: c.text, margin: '0 0 8px' },
  sub:   { fontWeight: 400, color: c.textMuted },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th:    { ...tbl.th, textAlign: 'left', whiteSpace: 'nowrap' },
  thNum: { ...tbl.thR, whiteSpace: 'nowrap' },
  td:    { ...tbl.td, color: c.textStrong, whiteSpace: 'nowrap' },
  num:   { ...tbl.tdR, color: c.textStrong },
  weak:  { fontSize: '10px', color: c.warningText, cursor: 'help' },
  more:  { all: 'unset', cursor: 'pointer', fontSize: '11px', color: c.brand, marginTop: '8px' },
  empty: { ...tbl.td, textAlign: 'center', color: c.textMuted, padding: '18px' },
  foot:  { fontSize: '11px', color: c.textMuted, margin: '10px 0 0', lineHeight: 1.45 },
};
